// src/pages/TaxInformation.js
import React, {
  useEffect,
  useRef,
  useState,
} from "react";

import {
  useTranslation,
} from "react-i18next";

import api from "../axiosConfig";

import {
  useCompanySettingsData,
} from "../hooks/useCompanySettingsData";

import "../styles/TaxInformation.css";

const getTaxId = (
  tax,
  details
) =>
  tax?.vat_tax_id ||
  tax?.vatTaxId ||
  tax?.tax_id ||
  tax?.taxId ||
  details?.vat_tax_id ||
  details?.vatTaxId ||
  details?.tax_id ||
  details?.taxId ||
  "";

export default function TaxInformation({
  embedded = false,
  onSaved,
}) {
  const { t } =
    useTranslation(
      "tax_information"
    );

  const {
    data,
    refresh,
    patchSection,
  } =
    useCompanySettingsData();

  const tax =
    data?.tax ||
    null;

  const details =
    data?.details ||
    null;

  const dirtyRef =
    useRef(false);

  const [
    form,
    setForm,
  ] = useState(
    () => ({
      taxId:
        getTaxId(
          tax,
          details
        ),
    })
  );

  const [
    companyDetailsId,
    setCompanyDetailsId,
  ] = useState(
    tax?.id ??
      details?.id ??
      null
  );

  const [
    saving,
    setSaving,
  ] = useState(false);

  const [
    error,
    setError,
  ] = useState("");

  useEffect(() => {
    if (
      dirtyRef.current
    ) {
      return;
    }

    setCompanyDetailsId(
      tax?.id ??
        details?.id ??
        null
    );

    setForm({
      taxId:
        getTaxId(
          tax,
          details
        ),
    });
  }, [
    tax,
    details,
  ]);

  const setField = (
    key,
    value
  ) => {
    dirtyRef.current =
      true;

    setForm(
      (
        previous
      ) => ({
        ...previous,

        [key]:
          value,
      })
    );
  };

  const handleSubmit =
    async (
      event
    ) => {
      event.preventDefault();

      setError(
        ""
      );

      setSaving(
        true
      );

      try {
        if (
          !companyDetailsId
        ) {
          throw new Error(
            t(
              "errors.missing_company_id"
            )
          );
        }

        const taxId =
          (
            form.taxId ||
            ""
          )
            .trim()
            .toUpperCase();

        await api.put(
          `/company-details/${companyDetailsId}`,
          {
            vat_tax_id:
              taxId,
          }
        );

        dirtyRef.current =
          false;

        setForm({
          taxId,
        });

        patchSection(
          "tax",
          {
            id:
              companyDetailsId,

            vat_tax_id:
              taxId,
          }
        );

        patchSection(
          "details",
          {
            vat_tax_id:
              taxId,
          }
        );

        if (
          typeof onSaved ===
          "function"
        ) {
          await onSaved();
        } else {
          await refresh();
        }

        alert(
          t(
            "toasts.saved_success"
          )
        );
      } catch (submitError) {
        console.error(
          "[TaxInformation] save failed:",
          submitError
        );

        setError(
          t(
            "toasts.saved_error"
          )
        );
      } finally {
        setSaving(
          false
        );
      }
    };

  return (
    <div
      className={`tax-information ${
        embedded
          ? "tax-information--embedded"
          : ""
      }`}
    >
      <div className="tax-information__header">
        <h1 className="tax-information__title">
          {t(
            "title"
          )}
        </h1>

        <p className="tax-information__subtitle">
          {t(
            "subtitle"
          )}
        </p>
      </div>

      {error && (
        <div className="tax-information__error">
          {error}
        </div>
      )}

      <form
        className="tax-information__form"
        onSubmit={
          handleSubmit
        }
      >
        <div className="tax-information__grid">
          <div className="tax-information__field">
            <label className="tax-information__label">
              {t(
                "fields.taxId.label"
              )}
            </label>

            <input
              className="tax-information__input"
              required
              value={
                form.taxId
              }
              onChange={(
                event
              ) =>
                setField(
                  "taxId",
                  event.target.value.toUpperCase()
                )
              }
              placeholder={t(
                "fields.taxId.placeholder"
              )}
              autoCapitalize="characters"
            />

            <small className="tax-information__hint">
              {t(
                "hints.taxId"
              )}
            </small>
          </div>
        </div>

        <div className="tax-information__actions">
          <button
            type="submit"
            className="tax-information__btn tax-information__btn--primary"
            disabled={
              saving
            }
          >
            {saving
              ? t(
                  "actions.saving"
                )
              : t(
                  "actions.save"
                )}
          </button>

          <button
            type="button"
            className="tax-information__btn tax-information__btn--ghost"
            onClick={() =>
              window.history.back()
            }
            disabled={
              saving
            }
          >
            {t(
              "actions.cancel"
            )}
          </button>
        </div>
      </form>
    </div>
  );
}